import React, { useEffect, useState } from "react";
import { PDFDocument } from "pdf-lib";
import Form from "../Components/Form";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase.config";
import { storage } from "../firebase.config";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { v4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { formatDateString, slugify } from "../utils/helperFunctions";
import { setLoading } from "../Redux/Loader/loadingSlice";

// ✅ use the centralized helper that sets createdAt/updatedAt
import { addExhibit } from "../firebase.config";

const Exhibit = ({
  pdfExhibit,
  setPdfExhibit,
  currentDate,
  imgExhibit,
  setImgExhibit,
  setReviewExhibitID,
}) => {
  const user = useSelector((state) => state.user);
  const [pdfURL, setPdfURL] = useState(null);
  const navigate = useNavigate();

  const newDate = new Date();
  const timeStamp = newDate.toJSON().replaceAll(":", "-").replaceAll(".", "-");
  const sluggedCompany = slugify(user.companyName);

  const [imageUpload, setImageUPload] = useState(null);
  const [fileUploads, setFileUploads] = useState([]);
  const [fileUrls, setFileUrls] = useState([]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!auth.currentUser) navigate("/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fillForm(exhibitDataForm, status) {
    dispatch(setLoading(true));
    try {
      // ---- Load the base PDF and grab fields
      const formUrl =
        "https://www.mlinc.collinicoding.com/ComplyOfficeExhibit.pdf";
      const formPdfBytes = await fetch(formUrl).then((res) =>
        res.arrayBuffer()
      );
      const pdfDoc = await PDFDocument.load(formPdfBytes);
      const form = pdfDoc.getForm();

      const serviceProviderLessor = form.getTextField(
        "Service Provider Lessor"
      );
      const commonName = form.getTextField("Common/Branded Name");
      const address = form.getTextField("Location Address");
      const city = form.getTextField("City");
      const state = form.getTextField("State");
      const zipCode = form.getTextField("Zip Code");
      const description = form.getTextField("Additional Description");
      const startDate = form.getTextField("Expected Start Date");
      const sqftLeased = form.getTextField("Square Feet Leased");
      const expectedTerm = form.getTextField("Expected Term of Lease");
      const numberOfDesks = form.getTextField("Number of Desks/Workstations");
      const companyName = form.getTextField("Company Lessee");
      const signature = form.getField("Signature");
      const signDate = form.getField("Date Signed");

      const office = form.getCheckBox("Office");
      const industrial = form.getCheckBox("Industrial/Warehouse");
      const retail = form.getCheckBox("Retail");
      const mixed = form.getCheckBox("Mixed/Other");
      const privateOffice = form.getCheckBox("Private Office");
      const cubicle = form.getCheckBox("Cubicle");
      const interior = form.getCheckBox("Interior");
      const exterior = form.getCheckBox("Exterior/Window");
      const cam = form.getCheckBox("Common area maintenance");
      const furnishings = form.getCheckBox("Furniture and furnishings");
      const janitorial = form.getCheckBox("Janitorial");
      const parking = form.getCheckBox("Parking");
      const insurance = form.getCheckBox("Property insurance and taxes");
      const electricity = form.getCheckBox("Electricity");
      const gas = form.getCheckBox("Gas/Oil/Propane");
      const water = form.getCheckBox("Water");
      const phone = form.getCheckBox("Phone");
      const internet = form.getCheckBox("Internet/WiFi");
      const conference = form.getCheckBox("Furnished conference room(s) use");
      const kitchen = form.getCheckBox("Kitchen");
      const copyFax = form.getCheckBox("Copy/fax/printer/scanner use");
      const receptionist = form.getCheckBox("Receptionist/Answering");

      // ---- Build the exhibit object (display dates stay as strings)
      const exhibit = {
        serviceProvider: exhibitDataForm.serviceProvider ?? "",
        commonName: exhibitDataForm.commonName ?? "",
        locationAddress: exhibitDataForm.locationAddress ?? "",
        city: exhibitDataForm.city ?? "",
        state: exhibitDataForm.state ?? "",
        zipcode: exhibitDataForm.zipcode ?? "",
        sqftLeased: exhibitDataForm.sqftLeased ?? "",
        term: exhibitDataForm.term ?? "",
        desks: exhibitDataForm.desks ?? "",
        propertyType: exhibitDataForm.propertyType ?? "",
        spaceType: exhibitDataForm.spaceType ?? "",
        settingType: exhibitDataForm.settingType ?? "",
        facility: exhibitDataForm.facility ?? [],
        utility: exhibitDataForm.utility ?? [],
        amenity: exhibitDataForm.amenity ?? [],
        description: exhibitDataForm.description ?? "",
        startDate: exhibitDataForm.startDate ?? "",
        companyName: user.companyName,
        signature: `${user.contactFirstName} ${user.contactLastName}`,
        signDate: formatDateString(currentDate), // display
        createdDate: formatDateString(currentDate), // display
        status: status ?? exhibitDataForm.status ?? "Saved",
        pdfURL: null,
        fileUrls: [],
      };

      // ---- Fill PDF fields safely
      serviceProviderLessor.setText(String(exhibit.serviceProvider || ""));
      commonName.setText(String(exhibit.commonName || ""));
      address.setText(String(exhibit.locationAddress || ""));
      city.setText(String(exhibit.city || ""));
      state.setText(String(exhibit.state || ""));
      zipCode.setText(String(exhibit.zipcode || ""));
      description.setText(String(exhibit.description || ""));
      startDate.setText(String(exhibit.startDate || ""));
      expectedTerm.setText(String(exhibit.term || ""));
      numberOfDesks.setText(String(exhibit.desks || ""));
      companyName.setText(String(exhibit.companyName || ""));
      signature.setText(String(exhibit.signature || ""));
      signDate.setText(String(exhibit.signDate || ""));

      // Checkboxes
      switch (exhibit.propertyType) {
        case "Office":
          office.check();
          break;
        case "Industrial/Warehouse":
          industrial.check();
          break;
        case "Retail":
          retail.check();
          break;
        case "Mixed/Other":
          mixed.check();
          break;
        default:
          break;
      }
      switch (exhibit.spaceType) {
        case "Private office":
          privateOffice.check();
          break;
        case "Cubicle":
          cubicle.check();
          break; // normalized "Cubicle"
        default:
          break;
      }
      switch (exhibit.settingType) {
        case "Interior":
          interior.check();
          break;
        case "Window":
          exterior.check();
          break;
        default:
          break;
      }

      (exhibit.facility || []).forEach((item) => {
        switch (item) {
          case "cam":
            cam.check();
            break;
          case "furnished":
            furnishings.check();
            break;
          case "janitorial":
            janitorial.check();
            break;
          case "parking":
            parking.check();
            break;
          case "tax":
            insurance.check();
            break;
          default:
            break;
        }
      });

      (exhibit.utility || []).forEach((item) => {
        switch (item) {
          case "electricity":
            electricity.check();
            break;
          case "gas":
            gas.check();
            break;
          case "water":
            water.check();
            break;
          case "phone":
            phone.check();
            break;
          case "internet":
            internet.check();
            break;
          default:
            break;
        }
      });

      (exhibit.amenity || []).forEach((item) => {
        switch (item) {
          case "meeting":
            conference.check();
            break;
          case "kitchen":
            kitchen.check();
            break;
          case "copy":
            copyFax.check();
            break;
          case "receptionist":
            receptionist.check();
            break;
          default:
            break;
        }
      });

      // ---- Save filled PDF and upload
      const pdfBytes = await pdfDoc.save();
      const filledPdfFile = new File([pdfBytes], "ComplyOfficeExhibit.pdf", {
        type: "application/pdf",
      });

      const pdfRef = ref(
        storage,
        `${sluggedCompany}/pdfs/complyOfficeExhibit-${timeStamp}.pdf`
      );

      const pdfTask = uploadBytesResumable(pdfRef, filledPdfFile);

      const pdfURL = await new Promise((resolve, reject) => {
        pdfTask.on(
          "state_changed",
          (snap) => {
            const pct = Math.round(
              (snap.bytesTransferred / snap.totalBytes) * 100
            );
            console.log(`PDF upload is ${pct}% done`);
          },
          reject,
          async () => {
            const url = await getDownloadURL(pdfTask.snapshot.ref);
            console.log("PDF available at:", url);
            resolve(url);
          }
        );
      });

      exhibit.pdfURL = pdfURL;
      setPdfURL(pdfURL);

      // ---- Upload any additional attachments, gather URLs
      const fileUrlPromises = (fileUploads || []).map((file, index) => {
        const fileName = file.name || file.label || `file_${index}`;
        const storageRef = ref(
          storage,
          `${sluggedCompany}/uploads/${fileName}-${v4()}`
        );
        const task = uploadBytesResumable(storageRef, file);

        return new Promise((resolve, reject) => {
          task.on(
            "state_changed",
            (snap) => {
              const pct = Math.round(
                (snap.bytesTransferred / snap.totalBytes) * 100
              );
              console.log(`Upload #${index + 1} is ${pct}% done`);
            },
            (err) => {
              console.error(`Upload #${index + 1} error:`, err);
              reject(err);
            },
            async () => {
              const url = await getDownloadURL(task.snapshot.ref);
              console.log(`Upload #${index + 1} URL: ${url}`);
              resolve(url);
            }
          );
        });
      });

      const attachmentUrls = await Promise.all(fileUrlPromises);
      exhibit.fileUrls = attachmentUrls;
      setFileUrls(attachmentUrls);

      // ---- Create the exhibit (serverTimestamp fields set in helper)
      const newId = await addExhibit(exhibit);
      setReviewExhibitID(newId);

      // Navigate after create
      if (exhibit.status === "Saved") {
        navigate("/dashboard");
      } else {
        navigate("/review");
      }
    } catch (err) {
      console.error("[Exhibit.fillForm] ERROR:", err);
    } finally {
      // ✅ ensure we actually dispatch here
      dispatch(setLoading(false));
    }
  }

  return (
    <div>
      <Form
        uploadImage={() => {}}
        imageUpload={imageUpload}
        setImageUPload={setImageUPload}
        setFileUploads={setFileUploads}
        fileUploads={fileUploads}
        setFileUrls={setFileUrls}
        fileUrls={fileUrls}
        currentDate={currentDate}
        fillForm={fillForm}
        setReviewExhibitID={setReviewExhibitID}
      />
    </div>
  );
};

export default Exhibit;
